import { ViewIcon } from "@chakra-ui/icons";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  useDisclosure,
  IconButton,
  Text,
  Image,
  Box,
  Textarea,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import axios from "axios";
import { useState } from "react";
import { ChatState } from "../../Context/ChatProvider";
import ChangePasswordModal from "./ChangePasswordModal";

const REPORT_REASONS = [
  "Spam",
  "Harassment",
  "Inappropriate content",
  "Impersonation",
  "Other",
];

const ProfileModal = ({ user: peer, children }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const { user: me, blockedIds, setBlockedIds, chats } = ChatState();
  const [blocking, setBlocking] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reason, setReason] = useState(REPORT_REASONS[0]);
  const [details, setDetails] = useState("");
  const [reporting, setReporting] = useState(false);

  if (!peer) return children ? <span onClick={onOpen}>{children}</span> : null;

  const isSelf = !!me?._id && String(peer._id) === String(me._id);
  const isBlocked = (blockedIds || []).some(
    (id) => String(id) === String(peer._id)
  );
  // Live "blocked by" state: the 1-1 chat carries peerBlockedMe, kept fresh
  // by fetchChats + the block socket events.
  const peerBlockedMe = (chats || []).some(
    (c) =>
      !c?.isGroupChat &&
      !!c?.peerBlockedMe &&
      (c.users || []).some((u) => String((u && u._id) || u) === String(peer._id))
  );

  const syncBlocked = (blocked) => {
    if (typeof setBlockedIds !== "function") return;
    setBlockedIds((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      if (blocked) {
        return list.some((id) => String(id) === String(peer._id))
          ? list
          : [...list, String(peer._id)];
      }
      return list.filter((id) => String(id) !== String(peer._id));
    });
  };

  const handleBlockToggle = async () => {
    if (!me?.token || !peer?._id) return;
    try {
      setBlocking(true);
      const config = {
        headers: { Authorization: `Bearer ${me.token}` },
      };
      const { data } = await axios.put(
        "/api/user/block",
        { userId: peer._id, block: !isBlocked },
        config
      );
      syncBlocked(!!data.blocked);
      toast({
        title: data.blocked
          ? `Blocked ${peer.name || "user"}`
          : `Unblocked ${peer.name || "user"}`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "bottom",
      });
    } catch (error) {
      toast({
        title: isBlocked ? "Failed to unblock" : "Failed to block",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    } finally {
      setBlocking(false);
    }
  };

  const handleReport = async () => {
    if (!me?.token || !peer?._id) return;
    if (!reason.trim()) {
      toast({
        title: "Pick a reason for the report",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "bottom",
      });
      return;
    }
    try {
      setReporting(true);
      const config = {
        headers: { Authorization: `Bearer ${me.token}` },
      };
      await axios.post(
        "/api/user/report",
        { userId: peer._id, reason: reason.trim(), details: details.trim() },
        config
      );
      // Reports auto-block, so reflect that immediately.
      syncBlocked(true);
      setShowReport(false);
      setDetails("");
      setReason(REPORT_REASONS[0]);
      toast({
        title: "Report submitted — user blocked",
        description: "Our team will review it. You won't hear from them.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    } catch (error) {
      toast({
        title: "Failed to submit report",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    } finally {
      setReporting(false);
    }
  };

  const handleClose = () => {
    setShowReport(false);
    onClose();
  };

  return (
    <>
      {children ? (
        <span onClick={onOpen}>{children}</span>
      ) : (
        <IconButton
          display={{ base: "flex" }}
          icon={<ViewIcon />}
          aria-label="View profile"
          variant="ghost"
          color="var(--ch-accent)"
          _hover={{ bg: "var(--ch-hover-ghost)" }}
          onClick={onOpen}
        />
      )}
      <Modal size="lg" onClose={handleClose} isOpen={isOpen} isCentered>
        <ModalOverlay bg="rgba(2,6,23,0.7)" backdropFilter="blur(6px)" />
        <ModalContent
          bg="var(--ch-modal)"
          color="var(--ch-text)"
          border="1px solid var(--ch-panel-border)"
          borderRadius="20px"
        >
          <ModalHeader
            fontSize="40px"
            fontFamily="Sora"
            fontWeight="800"
            display="flex"
            justifyContent="center"
          >
            {peer?.name || "Unknown"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            display="flex"
            flexDir="column"
            alignItems="center"
            justifyContent="space-between"
          >
            <Image
              borderRadius="full"
              boxSize="150px"
              src={peer?.pic}
              alt={peer?.name || "User"}
              border="2px solid"
              borderColor="rgba(34,211,238,0.6)"
              boxShadow="0 0 40px rgba(34,211,238,0.25)"
            />
            <Text
              fontSize={{ base: "28px", md: "30px" }}
              fontFamily="Work sans"
            >
              Email: {peer?.email || "—"}
            </Text>
            {!isSelf && isBlocked && (
              <Text fontSize="sm" color="var(--ch-danger-text)" mt={2}>
                You blocked this user — unblock to chat again.
              </Text>
            )}
            {!isSelf && peerBlockedMe && (
              <Text fontSize="sm" color="var(--ch-danger-text)" mt={2}>
                This user blocked you.
              </Text>
            )}
            {!isSelf && showReport && (
              <Box
                w="100%"
                mt={3}
                p={3}
                borderRadius="lg"
                bg="var(--ch-searchpanel)"
                border="1px solid var(--ch-item-border)"
              >
                <Text fontSize="sm" fontWeight="800" color="var(--ch-text)" mb={2}>
                  Report {peer?.name || "user"}
                </Text>
                <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
                  {REPORT_REASONS.map((r) => (
                    <Button
                      key={r}
                      size="xs"
                      variant={reason === r ? "solid" : "ghost"}
                      colorScheme={reason === r ? "cyan" : undefined}
                      color={reason === r ? "#06202a" : "var(--ch-text)"}
                      _hover={{ bg: "var(--ch-hover-ghost)" }}
                      onClick={() => setReason(r)}
                    >
                      {r}
                    </Button>
                  ))}
                </Box>
                <Textarea
                  size="sm"
                  className="chat-input"
                  placeholder="What happened? (optional)"
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  rows={3}
                />
                <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
                  <Button
                    size="sm"
                    variant="ghost"
                    color="var(--ch-text)"
                    onClick={() => setShowReport(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="red"
                    onClick={handleReport}
                    isLoading={reporting}
                  >
                    Submit report
                  </Button>
                </Box>
              </Box>
            )}
          </ModalBody>
          <ModalFooter gap={2}>
            {isSelf && (
              <ChangePasswordModal>
                <Button
                  variant="outline"
                  colorScheme="cyan"
                >
                  Change Password
                </Button>
              </ChangePasswordModal>
            )}
            {!isSelf && !showReport && (
              <>
                <Button
                  variant="ghost"
                  color="var(--ch-danger-text)"
                  _hover={{ bg: "rgba(255,0,0,0.12)" }}
                  onClick={() => setShowReport(true)}
                >
                  Report
                </Button>
                <Button
                  variant="outline"
                  colorScheme={isBlocked ? "cyan" : "red"}
                  onClick={handleBlockToggle}
                  isLoading={blocking}
                >
                  {isBlocked ? "Unblock" : "Block"}
                </Button>
              </>
            )}
            {reporting && <Spinner size="sm" color="var(--ch-seen)" />}
            <Button
              variant="ghost"
              color="var(--ch-text)"
              _hover={{ bg: "var(--ch-hover-ghost)" }}
              onClick={handleClose}
            >
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ProfileModal;
